import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { get, patch } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"
import { runCompetitionOperation } from "pages/EventsManagement/components/Tabs/Competitions/competitionOperation"
export const reportMessage = code => ({
  ERROR_PERMISSIONS: "Your account no longer has permission to manage reports.",
  REPORT_REVISION_CONFLICT: "This report changed. Review its current status before trying again.",
  INVALID_REPORT_TRANSITION: "This action is no longer available for the current report status.",
  OPERATION_PAYLOAD_CONFLICT: "This identifier belongs to another request. Check its saved result.",
  INVALID_REPORT_ACTION: "The report action was refused. Review the current report before saving again; a note needs text.",
  REPORT_NOT_FOUND: "This report is no longer available.",
}[code] || "The action could not be confirmed. Check its saved result before trying again.")
export default function useReportOperation({ actorId, reportId }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const key = `report-operation:${actorId}:${reportId}`
  const active = useRef(null), retained = useRef(null), [state, setState] = useState({})
  const persist = useCallback(operation => {
    try { sessionStorage.setItem(key, JSON.stringify(operation)); return true } catch { return false }
  }, [key])
  // A 4xx on the first send was refused before anything was queued, so the runner ends it as an error with words; Check result
  // and retry (firstSubmission false) cannot rule out an earlier commit and stays unknown.
  const observe = useCallback((operation, submit, firstSubmission = false) => {
    active.current?.cancel()
    const runner = runCompetitionOperation({ operation, socket, submit, firstSubmission, scopeKeys: ["reportId"], responseTopic: "accounts-response", operationKind: "report-managed", messageForCode: reportMessage,
      read: id => get(`/accounts/reports/${reportId}/operations/${id}`) })
    active.current = runner; retained.current = operation
    setState({ status: "pending", operation })
    runner.promise.then(result => {
      if (active.current !== runner || result.status === "cancelled") return
      active.current = null
      setState({ ...result, operation })
      if (result.status === "saved") {
        retained.current = null
        try { sessionStorage.removeItem(key) } catch { /* The confirmed receipt remains available on the server. */ }
      } else if (result.status === "error") {
        retained.current = { ...operation, recovery: result }; persist(retained.current)
      }
    })
  }, [reportId, socket, key, persist])
  useEffect(() => {
    setState({}); retained.current = null
    if (actorId) try {
      const operation = JSON.parse(sessionStorage.getItem(key) || "null")
      if (operation?.actorId === actorId && operation.reportId === reportId && /^[a-zA-Z0-9-]{16,128}$/.test(operation.operationId || "")
        && operation.correlationId && operation.payload && ["resolve", "reopen", "note"].includes(operation.action)) {
        operation.recovered = true
        retained.current = operation
        if (operation.recovery?.status === "error") setState({ ...operation.recovery, operation })
        else observe(operation)
      }
    } catch { /* Invalid local storage cannot initiate a write. */ }
    return () => { active.current?.cancel(); active.current = null }
  }, [actorId, reportId, key, observe])
  const send = operation => () => patch(`/accounts/reports/${reportId}`, { ...operation.payload, operationId: operation.operationId, socketClientId }, { headers: { "X-Correlation-Id": operation.correlationId } })
  const save = payload => {
    if (!actorId || active.current || retained.current) return
    const operation = { actorId, reportId, action: payload.action, operationId: newPlayerOperationId(), correlationId: newPlayerOperationId(), payload }
    if (!persist(operation)) { setState({ status: "error", message: "Session storage is unavailable. Enable it before saving so this action can be recovered." }); return }
    observe(operation, send(operation), true)
  }
  const review = () => {
    if (active.current || (retained.current && retained.current.recovery?.status !== "error")) return
    try { sessionStorage.removeItem(key) } catch { return }
    retained.current = null; setState({})
  }
  return { ...state, save, review, pending: ["pending", "unknown"].includes(state.status),
    recover: () => state.operation && ["pending", "unknown"].includes(state.status) && observe(state.operation, send(state.operation)) }
}
